/**
 * @render react
 * @name Tooltip
 * @example
 * <Tooltip title="Tooltip title" content="Tooltip content" icon="info" closeLabel="Close" />
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Heading } from 'Components/molecules/Heading';

import './_style.scss';

/**
 * Tooltip component
 * @param {string} icon - Icon to be shown for the component
 * @param {string} title - Title of the component
 * @param {string} content - Content of the component
 * @param {string} closeLabel - Label for close link
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */

const Tooltip = (
  {
    icon,
    title,
    content,
    closeLabel,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
  },
) => {
  const [isOpen, setOpen] = useState(false);
  const [isHovered, setHover] = useState(false);
  let tooltipClickTimer;
  let tooltipHoverTimer;

  useEffect(() => () => {
    clearTimeout(tooltipClickTimer);
    clearTimeout(tooltipHoverTimer);
  }, []);

  /**
   * Opens the tooltip
   */
  const openTooltip = () => {
    setOpen(true);
  };

  /**
   * Closes the tooltip
   */
  const closeTooltip = () => {
    if (isHovered) {
      clearTimeout(tooltipHoverTimer);
    }

    setOpen(false);
    setHover(false);
  };

  /**
   * Closes the tooltip with 500ms delay
   */
  const closeDelayTooltip = () => {
    tooltipClickTimer = setTimeout(() => {
      setOpen(false);
    }, 500);
  };

  /**
   * Toggles the tooltip
   */
  const toggleTooltip = () => {
    setOpen(!isOpen);
  };

  /**
   * Sets hover state to true
   */
  const onContentEnter = () => {
    setHover(true);
  };

  /**
   * Sets hover state to false with 500ms delay
   */
  const onContentLeave = () => {
    tooltipHoverTimer = setTimeout(() => {
      setHover(false);
    }, 500);
  };

  return (
    <button
      className={classNames({
        [`${cssPrefix}__tooltip`]: loadStyles,
        ...extraClassNames,
      })}
      type="button"
      onClick={toggleTooltip}
      onMouseEnter={openTooltip}
      onMouseLeave={closeDelayTooltip}
    >
      <span
        className={`${cssPrefix}__tooltip--icon`}
      >
        {icon ? <Icon icon={icon} /> : 'ℹ'}
      </span>
      <div className={`${cssPrefix}__tooltip`}>
        <div
          onMouseEnter={onContentEnter}
          onMouseLeave={onContentLeave}
          className={classNames({
            [`${cssPrefix}__tooltip--container`]: true,
            '-open': isOpen,
            '-hovered': isHovered,
          })}
        >
          {title && (
            <Heading size={6} extraClassNames={{ [`${cssPrefix}__tooltip--title`]: true }}>{title}</Heading>
          )}
          {content && (
            <div className={`${cssPrefix}__tooltip--content`}>
              {content}
            </div>
          )}
          <div
            className={`${cssPrefix}__tooltip--close`}
            onClick={closeTooltip}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') closeTooltip(); }}
            role="button"
            tabIndex={0}
          >
            {closeLabel}
          </div>
        </div>
      </div>
    </button>
  );
};

/**
 * Valid Props
 */
Tooltip.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  closeLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default Props
 */
Tooltip.defaultProps = {
  icon: '',
  title: '',
  content: '',
  closeLabel: 'Close',
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(Tooltip);
